import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useNotify, useUpdate } from 'react-admin';
import { apiFetchResponse } from '../lib/apiFetch';

/** One value the DEC XML could not carry as stored (see nestjs dec-xsd.ts). */
export interface DecAdjustment {
  path: string;
  label: string;
  original: string;
  value: string;
  maxLength?: number;
  kind: 'shortened' | 'truncated' | 'dropped';
  source?: 'override' | 'place_dec_name';
}

const PLACE_OF_DEATH = 'Deces/NomLieuDeces';
const PLACE_OF_DEATH_MAX = 35;

/** The sentence staff read for one adjustment. */
export const describeAdjustment = (a: DecAdjustment): string => {
  if (a.kind === 'dropped') {
    return `${a.label} “${a.original}” doesn't match the format the government form accepts, so the XML leaves it blank. Correct it on the case, or enter it in SIED.`;
  }
  if (a.source === 'override') return `${a.label}: using the DEC name you entered: “${a.value}”.`;
  const how =
    a.source === 'place_dec_name'
      ? "using the facility's short name"
      : a.kind === 'truncated'
      ? 'cut to'
      : 'shortened to';
  return `${a.label} is ${a.original.length} characters; the government form allows ${a.maxLength} — ${how}: “${a.value}”.`;
};

/**
 * Values the DEC XML had to shorten or leave blank to pass the government
 * schema, shown above "Download DEC XML" so staff check them before uploading.
 * The place-of-death name is editable here (funerals.dec_place_of_death_name).
 */
const DecSchemaAdjustments: React.FC<{ funeralId: string | number; refreshKey?: unknown }> = ({
  funeralId,
  refreshKey,
}) => {
  const [adjustments, setAdjustments] = useState<DecAdjustment[]>([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [updateFuneral, { isLoading: saving }] = useUpdate();
  const notify = useNotify();

  const load = async () => {
    try {
      const response = await apiFetchResponse(`validation/death-declaration-xml/${funeralId}/adjustments`);
      const body = await response.json();
      setAdjustments(body?.success ? body.data ?? [] : []);
    } catch {
      setAdjustments([]);
    }
  };

  useEffect(() => {
    void load();
  }, [funeralId, refreshKey]);

  const saveName = (name: string | null) => {
    void updateFuneral(
      'funerals',
      { id: funeralId, data: { dec_place_of_death_name: name }, previousData: { id: funeralId } },
      {
        onSuccess: () => {
          setEditing(false);
          notify(name ? 'DEC place-of-death name saved' : 'DEC place-of-death name reset', { type: 'success' });
          void load();
        },
        onError: (e: any) => notify(e?.message || 'Could not save the DEC name', { type: 'error' }),
      },
    );
  };

  if (adjustments.length === 0) return null;

  const severity = adjustments.some((a) => a.kind !== 'shortened') ? 'warning' : 'info';

  return (
    <Alert severity={severity} sx={{ mt: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Check before uploading: the government form has length and format limits
      </Typography>
      <Stack spacing={1}>
        {adjustments.map((a) => (
          <Box key={a.path}>
            <Typography variant="body2">{describeAdjustment(a)}</Typography>
            {a.path === PLACE_OF_DEATH &&
              (editing ? (
                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 0.5 }}>
                  <TextField
                    size="small"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    inputProps={{ maxLength: PLACE_OF_DEATH_MAX }}
                    helperText={`${draft.length}/${PLACE_OF_DEATH_MAX}`}
                    sx={{ minWidth: 300, bgcolor: 'background.paper' }}
                    autoFocus
                  />
                  <Button size="small" variant="contained" disabled={saving || !draft.trim()} onClick={() => saveName(draft.trim())}>
                    Save
                  </Button>
                  <Button size="small" onClick={() => setEditing(false)}>
                    Cancel
                  </Button>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setDraft(a.value);
                      setEditing(true);
                    }}
                  >
                    Edit DEC name
                  </Button>
                  {a.source === 'override' && (
                    <Button size="small" disabled={saving} onClick={() => saveName(null)}>
                      Reset
                    </Button>
                  )}
                </Stack>
              ))}
          </Box>
        ))}
      </Stack>
    </Alert>
  );
};

export default DecSchemaAdjustments;
